/**
 * Turning a supplier's quotation into material order lines.
 *
 * Somebody uploads the PDF the supplier emailed over and the order fills
 * itself in. Jonathon, 2026-09-01: "there needs to be a feature where we can
 * upload material order estimates in the material order section, it reads the
 * document and enters the line items with their quantity and price. that will
 * help streamline entering in material orders."
 *
 * The reading is done by the model, on the server, where the API key lives.
 * What is here is everything that happens to the answer afterwards, and the
 * hard part is not the arithmetic — it is that a supplier writes
 * `2.6" Iso 4x8/Pc` and the catalogue says `2.6" ISO 4'x8'`, and those have to
 * be recognised as the same thing without `TPO Cleaner` being matched to
 * `TPO Mem. Cleaner` when they are two different products at different prices.
 *
 * ## What this refuses to do
 *
 * **It never invents a product.** A line it cannot place keeps the supplier's
 * own wording and its price, with no `product_id`, which
 * `material_order_items` allows. That line costs the right money and orders the
 * right thing; it is simply not linked to the catalogue. Creating products from
 * a PDF would fill the catalogue with near-duplicates of things already in it,
 * and every one of them would be a reference value nobody can rename (rule 8).
 *
 * **It never silently guesses.** Every line comes back with the evidence for
 * its match, and anything below `CONFIDENT` is offered for a person to confirm
 * rather than applied. A wrong product on a material order is money spent on
 * the wrong thing, and it is invisible once the order is placed.
 */

import { type Cents, rateTimesQty, sumBy, toUnitRate } from "./money.js";

/** One line as the supplier printed it, before anything is matched. */
export interface QuotedLine {
  /** What the quote calls it, verbatim. */
  description: string;
  qty: number;
  /** "Rolls", "pcs", "Boxes" — the supplier's own word, when there is one. */
  unit?: string | null;
  /** Price for one, in dollars as printed. */
  unitPrice: number;
}

/** A product this could be matched against. Anything with these fields will do. */
export interface CatalogueProduct {
  id: string;
  name: string;
  unit?: string | null;
  active?: boolean | null;
}

/**
 * How sure the match is, and therefore what the screen does with it.
 *
 * Three levels rather than a number, because the number would be read as
 * precision it does not have. What matters is the decision: apply it, ask about
 * it, or leave the line unlinked.
 */
export type MatchConfidence = "confident" | "possible" | "none";

export interface MatchedLine extends QuotedLine {
  product: CatalogueProduct | null;
  confidence: MatchConfidence;
  /** Why it matched, for the review screen. Never for the machine to parse. */
  why: string;
  /** Runners-up, so a person correcting a match has somewhere to start. */
  alternatives: readonly CatalogueProduct[];
}

/* -------------------------------------------------------------------------
 * Reading a supplier's wording
 * ---------------------------------------------------------------------- */

/**
 * Words that carry no meaning in a product name and only add false overlap.
 *
 * `/Roll`, `/Pc` and `1000/Box` say how it is packaged, which the unit column
 * already says. Left in, every roll on the quote gains a point of similarity
 * with every roll in the catalogue.
 */
const NOISE = new Set([
  "roll",
  "rolls",
  "pc",
  "pcs",
  "piece",
  "pieces",
  "box",
  "boxes",
  "bx",
  "ea",
  "each",
  "pail",
  "pails",
  "sheet",
  "sheets",
  "per",
  "the",
  "and",
  "for",
  "of",
]);

/**
 * The same word however either side spells it.
 *
 * Deliberately short. Every entry is a judgement that two spellings mean one
 * thing, and a wrong one silently merges two products — so this holds only
 * pairs seen on real documents.
 */
const SYNONYMS = new Map<string, string>([
  ["iso", "iso"],
  ["isocyanurate", "iso"],
  ["ins", "insulation"],
  ["insul", "insulation"],
  ["adh", "adhesive"],
  ["adhes", "adhesive"],
  ["mem", "membrane"],
  ["memb", "membrane"],
  ["fastener", "fasteners"],
  ["plate", "plates"],
  ["galv", "galvanized"],
  ["gal", "gallon"],
  ["sq", "square"],
  ["lf", "feet"],
  ["verisco", "versico"],
]);

/**
 * A product name reduced to the words and numbers that identify it.
 *
 * Inches and feet marks are the whole difficulty: a supplier writes `4x8/Pc`
 * and the catalogue writes `4'x8'`, and `.060ML` against `.060`. So every kind
 * of quote mark goes, `x` between two numbers becomes a separator, and what is
 * left is words and bare numbers.
 */
export function normalizeTokens(text: string): string[] {
  const cleaned = (text ?? "")
    .toLowerCase()
    // Curly and straight quotes, both meanings, all gone.
    .replace(/[‘’“”'"`]/g, " ")
    .replace(/[®™©]/g, " ")
    // 4x8 and 10'x100' are two measurements, not one word.
    .replace(/(\d)\s*x\s*(\d)/g, "$1 $2")
    .replace(/[^a-z0-9.]+/g, " ");

  const out: string[] = [];
  for (const raw of cleaned.split(/\s+/)) {
    const word = raw.replace(/^\.+|\.+$/g, "");
    if (word === "") continue;
    if (NOISE.has(word)) continue;
    // A bare number keeps its value: 2.6 and 060 are what tell two membranes
    // or two boards apart, and they are the tokens most worth matching on.
    out.push(SYNONYMS.get(word) ?? word);
  }
  return out;
}

/** Numbers in a name — 2.6, 4, 8, 060. What actually distinguishes products. */
const numbersIn = (tokens: readonly string[]): string[] =>
  tokens.filter((t) => /^[\d.]+$/.test(t)).map((t) => String(Number(t)));

/**
 * How alike two names are, from 0 to 1.
 *
 * Words and numbers are scored separately and the numbers count for more.
 * "TPO Cleaner" and "TPO Mem. Cleaner" share every word either has; what tells
 * two boards apart is the 2.6 against the 1.5, and what tells two rolls apart
 * is the 10 against the 5. A score built on words alone confidently matches the
 * wrong one of a pair, which is the failure this whole module exists to avoid.
 */
export function similarity(a: readonly string[], b: readonly string[]): number {
  if (a.length === 0 || b.length === 0) return 0;

  const wordsA = new Set(a.filter((t) => !/^[\d.]+$/.test(t)));
  const wordsB = new Set(b.filter((t) => !/^[\d.]+$/.test(t)));
  const wordScore = overlap(wordsA, wordsB);

  const numsA = new Set(numbersIn(a));
  const numsB = new Set(numbersIn(b));
  // Neither side carrying a number is not agreement about numbers; it is the
  // absence of evidence, so it neither helps nor hurts.
  if (numsA.size === 0 && numsB.size === 0) return wordScore;

  const numScore = overlap(numsA, numsB);
  return wordScore * 0.45 + numScore * 0.55;
}

/** Jaccard: how much of everything either side has, both of them have. */
function overlap(a: ReadonlySet<string>, b: ReadonlySet<string>): number {
  if (a.size === 0 && b.size === 0) return 0;
  let shared = 0;
  for (const item of a) if (b.has(item)) shared += 1;
  const union = a.size + b.size - shared;
  return union === 0 ? 0 : shared / union;
}

/** At or above this, the match is applied. */
export const CONFIDENT_AT = 0.7;
/** At or above this, it is offered for a person to confirm. */
export const POSSIBLE_AT = 0.4;
/** How much clear water a winner needs before it is trusted outright. */
export const MUST_BEAT_RUNNER_UP_BY = 0.1;
/**
 * At or above this, a product is worth putting in front of a person.
 *
 * Deliberately far below `POSSIBLE_AT`. An alternative is only ever shown — it
 * is never applied — so the cost of offering a wrong one is a line in a
 * dropdown, while the cost of withholding the right one is somebody hunting the
 * whole catalogue by hand. "TPO Cleaner" scores the 1-gallon pail at 0.67 and
 * the 20-square pail at 0.22, and those are the two a person is choosing
 * between.
 */
export const SHOW_ALTERNATIVE_AT = 0.15;

/**
 * The product a quoted line is describing, if the catalogue has one.
 *
 * A winner has to be both good enough *and* clearly better than the next
 * candidate. Two products a hair apart is exactly the case where the machine
 * should not choose — `TPO Mem. Cleaner (20 squares)` and `TPO Cleaner
 * 1Gal./Pail` score within a whisker of each other against `TPO Cleaner`, and
 * they are different products at $157 and $38.75.
 */
export function matchQuotedLine(
  line: QuotedLine,
  catalogue: readonly CatalogueProduct[],
): MatchedLine {
  const wanted = normalizeTokens(line.description);
  const scored = catalogue
    .filter((p) => p.active !== false)
    .map((product) => ({
      product,
      score: similarity(wanted, normalizeTokens(product.name)),
    }))
    .sort((a, b) => b.score - a.score);

  const best = scored[0];
  const runnerUp = scored[1];
  const alternatives = scored
    .filter((s) => s.score >= SHOW_ALTERNATIVE_AT)
    .slice(0, 5)
    .map((s) => s.product);

  if (!best || best.score < POSSIBLE_AT)
    return {
      ...line,
      product: null,
      confidence: "none",
      why: "Nothing in the catalogue looks like this. The line keeps the supplier's wording and price.",
      alternatives: [],
    };

  const clear =
    !runnerUp || best.score - runnerUp.score >= MUST_BEAT_RUNNER_UP_BY;

  if (best.score >= CONFIDENT_AT && clear)
    return {
      ...line,
      product: best.product,
      confidence: "confident",
      why: `Matched "${best.product.name}".`,
      alternatives: alternatives.filter((p) => p.id !== best.product.id),
    };

  return {
    ...line,
    product: best.product,
    confidence: "possible",
    why: clear
      ? `Looks like "${best.product.name}", but not closely enough to be sure.`
      : `Could be "${best.product.name}" or "${runnerUp!.product.name}" — they score alike.`,
    alternatives: alternatives.filter((p) => p.id !== best.product.id),
  };
}

/* -------------------------------------------------------------------------
 * What lands on the order
 * ---------------------------------------------------------------------- */

/** One row ready for `material_order_items`. */
export interface ImportedRow {
  product_id: string | null;
  /** The supplier's wording, kept whether or not a product was matched. */
  name: string;
  qty: number;
  unit: string | null;
  unit_price: number;
  sort_order: number;
}

export interface ImportPlan {
  rows: readonly ImportedRow[];
  matched: readonly MatchedLine[];
  /** What the quote totals, in cents, for checking against the paper it came off. */
  total: Cents;
  counts: Record<MatchConfidence, number>;
}

/**
 * Everything the review screen needs, from the model's answer and the
 * catalogue.
 *
 * Lines with no quantity or no price are kept, not dropped. A supplier's
 * quotation carries subtotals, delivery lines and the occasional blank, and a
 * line silently discarded is the one nobody notices is missing until the truck
 * arrives short. They come through with zeroes for a person to deal with.
 *
 * The name on the row is always the supplier's, even on a confident match. The
 * catalogue name is what the company calls it; the quote's name is what the
 * supplier will put on the invoice, and reconciling the two is the whole job of
 * the person reading this screen.
 */
export function planImport(
  lines: readonly QuotedLine[],
  catalogue: readonly CatalogueProduct[],
  startingSortOrder = 100,
): ImportPlan {
  const matched = lines.map((line) => matchQuotedLine(line, catalogue));
  const counts: Record<MatchConfidence, number> = {
    confident: 0,
    possible: 0,
    none: 0,
  };
  for (const line of matched) counts[line.confidence] += 1;

  const rows = matched.map((line, i) => ({
    // A "possible" match is not applied. The screen offers it and a person
    // says yes; until then the line is unlinked and still correct.
    product_id: line.confidence === "confident" ? line.product!.id : null,
    name: line.description.trim(),
    qty: safeNumber(line.qty),
    unit: line.unit?.trim() || null,
    unit_price: safeNumber(line.unitPrice),
    sort_order: startingSortOrder + i * 10,
  }));

  return {
    rows,
    matched,
    // Each line is rate x qty in cents, then the cents are summed. Adding
    // the model's dollar figures as floats drifts by a cent on a long quote,
    // and a cent is exactly what this total exists to catch (rule 3).
    total: sumBy(rows, (r) => rateTimesQty(toUnitRate(r.unit_price), r.qty)),
    counts,
  };
}

/** A number that reached us through JSON from a model. Never NaN, never below zero. */
function safeNumber(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
}
